import React, { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  CheckboxV2,
  Flex,
  Spinner,
  useToastContext,
} from '@resideo/blueprint-react';

import ModalHeader from 'components/common/ModalHeader';
import PageLoading from 'components/common/PageLoading';
import Typography from 'components/common/Typography';

import { useDevices } from 'hooks/useDataStream';
import { useRisClient } from 'hooks/useRisClient';
import { useAuth } from 'utils/auth';
import { FIRST_ALERT_CONNECTED_ACCOUNTS } from 'config/flags';

import {
  PartnerCloudAccount,
  PartnerCloudDeviceConnectRequest,
  PartnerCloudDevice,
  PartnerCloudUserAuthResponse,
  StepComponentProps,
  RisError,
} from 'types/types';

import {
  DeviceEntryContainer,
  ScrollableArea,
} from '../../DataStreamScreen.style';

const { Text } = Typography;

const DeviceSelectionStep = ({
  account,
  userSession,
  closeModal,
}: StepComponentProps) => {
  const queryClient = useQueryClient();

  const { client } = useRisClient();
  const { getAccessTokenSilently } = useAuth();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const [selectedDevices, setSelectedDevices] = useState<
    Record<string, boolean>
  >({});

  // Session state: for First Alert, fetch from auth context; for others, use OAuth-provided userSession
  const [effectiveSession, setEffectiveSession] = useState<
    PartnerCloudUserAuthResponse | undefined
  >(userSession);

  const [isLoadingAuth, setIsLoadingAuth] = useState(false);

  // Sync effectiveSession with userSession for non-First Alert partners
  useEffect(() => {
    const isFirstAlert = account.partnerCloudName === 'First Alert';

    // For non-First Alert partners, keep effectiveSession in sync with userSession (including when it becomes undefined)
    if (!isFirstAlert) {
      setEffectiveSession(userSession);
    }
  }, [userSession, account.partnerCloudName]);

  // Fetch authentication data from auth context for First Alert on mount
  useEffect(() => {
    const isFirstAlert = account.partnerCloudName === 'First Alert';

    // For First Alert, fetch session from current auth context. For other partners, OAuth flow provides userSession
    if (FIRST_ALERT_CONNECTED_ACCOUNTS && isFirstAlert) {
      let isMounted = true;

      const fetchAuthData = async () => {
        setIsLoadingAuth(true);
        try {
          // Get access token from Auth0
          const accessToken = await getAccessTokenSilently();

          // Get user ID from app's user endpoint
          const myUser = await client.myUsers.myUsersGetMyUser();
          const externalUserId = myUser.data?.id || '';

          if (isMounted) {
            setEffectiveSession({
              externalUserId,
              accessToken,
            } as PartnerCloudUserAuthResponse);
          }
        } catch (error) {
          console.error(
            '[DeviceSelectionStep] Failed to get auth data:',
            error
          );

          Sentry.captureEvent({
            message:
              '[Data Streaming] Failed to fetch First Alert authentication data',
            level: 'error',
            extra: { account, error },
          });

          if (isMounted) {
            addToast({
              toastType: 'Error',
              message: t('privacy.dataStream.errors.genericError'),
            });
          }
        } finally {
          if (isMounted) {
            setIsLoadingAuth(false);
          }
        }
      };

      fetchAuthData();

      return () => {
        isMounted = false;
      };
    }
    // Dependencies: Only account.partnerCloudName - hook functions (getAccessTokenSilently, client, addToast, t) are stable
  }, [account.partnerCloudName]);

  const {
    data: devices,
    error,
    isFetching,
  } = useDevices(account.partnerCloudId, effectiveSession);

  useEffect(() => {
    if (devices) {
      setSelectedDevices(
        devices.reduce((acc: Record<string, boolean>, device) => {
          // if device has isDataStreamingEnabled use it, otherwise default to false
          acc[device.deviceId] = !!device.isDataStreamingEnabled;
          return acc;
        }, {})
      );
    } else {
      setSelectedDevices({});
    }
  }, [devices]);

  useEffect(() => {
    if (error) {
      addToast({
        toastType: 'Error',
        message: t(
          'privacy.dataStream.accountSignIn.devices.errors.loadFailed'
        ),
      });

      Sentry.captureEvent({
        message: '[Data Streaming] Error loading partner devices',
        level: 'error',
        extra: { account },
      });
    }
  }, [error]);

  const connectDevices = useMutation({
    mutationKey: ['dataStream', 'connectDevices'],
    mutationFn: async ({
      partnerCloudId,
      requestBody,
    }: PartnerCloudDeviceConnectRequest) =>
      await client.partnerClouds.myUsersPostPartnerCloudDataStreamingDevices(
        partnerCloudId,
        requestBody
      ),

    onMutate: async ({ requestBody }) => {
      await queryClient.cancelQueries({ queryKey: ['dataStream', 'accounts'] });

      const previousAccounts = queryClient.getQueryData([
        'dataStream',
        'accounts',
      ]);

      const { devices: selected = [] } = requestBody || {};

      return { previousAccounts, selected };
    },

    onSuccess: (data, variables, context) => {
      addToast({
        toastType: 'Success',
        message: t('privacy.dataStream.success.accountConnected'),
      });

      queryClient.setQueryData(
        ['dataStream', 'accounts'],
        (old: PartnerCloudAccount[]) => {
          return old.map(oldAccountData => {
            if (oldAccountData.partnerCloudId === variables.partnerCloudId) {
              const uniqueDevices = [
                ...new Map(
                  [...oldAccountData.connectedDevices, ...context.selected].map(
                    obj => [obj.deviceId, obj]
                  )
                ).values(),
              ];

              return {
                ...oldAccountData,
                connectedDevices: uniqueDevices,
              };
            }

            return oldAccountData;
          });
        }
      );

      closeModal();
    },

    onError: (err: RisError, variables, context) => {
      queryClient.setQueryData(
        ['dataStream', 'accounts'],
        context?.previousAccounts
      );

      closeModal();

      addToast({
        toastType: 'Error',
        message: t('privacy.dataStream.errors.genericError'),
      });

      Sentry.captureEvent({
        message: '[Data Streaming] Error connecting partner devices',
        level: 'error',
        extra: { variables },
      });
    },

    onSettled: (
      data,
      err,
      { partnerCloudId, requestBody: { externalUserId } }
    ) => {
      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'devices', partnerCloudId, externalUserId],
        refetchType: 'none',
      });

      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'connectedDevices', partnerCloudId],
        refetchType: 'none',
      });
    },

    retry: false,
  });

  const onDeviceSelectionChange = (device: PartnerCloudDevice) => {
    setSelectedDevices(prev => ({
      ...prev,
      [device.deviceId]: !prev[device.deviceId],
    }));
  };

  const handleConnect = () => {
    if (!devices) return;

    const selectedDevicesData = devices.filter(
      device =>
        selectedDevices[device.deviceId] && !device.isDataStreamingEnabled
    );

    // requires a valid session (effectiveSession contains auth data for all partners)
    if (!effectiveSession) return;

    const requestBody = {
      accessToken: effectiveSession.accessToken,
      externalUserId: effectiveSession.externalUserId,
      devices: selectedDevicesData,
    };

    const payload: PartnerCloudDeviceConnectRequest = {
      partnerCloudId: account.partnerCloudId,
      requestBody: requestBody,
    };

    connectDevices.mutate(payload);
  };

  return (
    <>
      <ModalHeader
        title={t('privacy.dataStream.accountSignIn.devices.title', {
          accountName: account.partnerCloudName,
        })}
        closeModal={closeModal}
      />

      <Box>
        <Text marginBottom='medium'>
          {t('privacy.dataStream.accountSignIn.devices.description')}
        </Text>

        {(isFetching || isLoadingAuth) && <PageLoading />}

        {!isFetching && !isLoadingAuth && devices && devices.length > 0 && (
          <ScrollableArea>
            {devices.map(device => (
              <DeviceEntryContainer
                data-test='data-stream-device-list-entry'
                key={device.deviceId}
                alignItems='flex-start'>
                <CheckboxV2
                  checked={selectedDevices[device.deviceId] || false}
                  disabled={device.isDataStreamingEnabled}
                  onChange={() => onDeviceSelectionChange(device)}
                />

                <Flex flexDirection='column' marginLeft='15px'>
                  <Text strong='true' marginBottom='8px'>
                    {device.deviceName}
                  </Text>

                  <Text marginBottom='8px'>{device.deviceModel}</Text>

                  <Text>{device.deviceType}</Text>
                </Flex>
              </DeviceEntryContainer>
            ))}
          </ScrollableArea>
        )}

        {!isFetching && !isLoadingAuth && !devices?.length && (
          <Text>{t('privacy.dataStream.accountSignIn.devices.noDevices')}</Text>
        )}
      </Box>

      <Flex
        data-test='data-stream-sign-in-devices-actions'
        justifyContent='flex-end'
        paddingTop='large'>
        <Button
          disabled={
            connectDevices.isPending ||
            !devices?.some(
              device =>
                selectedDevices[device.deviceId] &&
                !device.isDataStreamingEnabled
            )
          }
          onClick={handleConnect}>
          {connectDevices.isPending ? (
            <Spinner aria-label={t('common.connecting')} color='white' />
          ) : (
            t('common.connect')
          )}
        </Button>
      </Flex>
    </>
  );
};

export default DeviceSelectionStep;
